import { useId, type SVGProps } from 'react';

export type StoreBadgeProps = {
  store: 'google-play' | 'app-store';
  /** The app's listing on that store. */
  href: string;
  className?: string;
};

function GooglePlayLogo(props: SVGProps<SVGSVGElement>) {
  // The four-colour Play triangle — two straight lines crossing on its
  // midline — clipped to a triangle with rounded corners.
  const clipId = useId();
  return (
    <svg viewBox="0 0 22 24" {...props}>
      <defs>
        <clipPath id={clipId}>
          <path d="M0 4Q0 0 3.51 1.92L18.49 10.08Q22 12 18.49 13.92L3.51 22.08Q0 24 0 20Z" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <path d="M0 0L11 12L0 24Z" fill="#4285F4" />
        <path d="M0 0L14.67 8L11 12Z" fill="#34A853" />
        <path d="M0 24L11 12L14.67 16Z" fill="#EA4335" />
        <path d="M11 12L14.67 8L22 12L14.67 16Z" fill="#FBBC04" />
      </g>
    </svg>
  );
}

function AppleLogo(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

const STORES = {
  'google-play': {
    caption: 'GET IT ON',
    name: 'Google Play',
    label: 'Get it on Google Play',
    Logo: GooglePlayLogo,
    logoClass: 'h-7 w-[26px]',
    captionClass: 'tracking-[0.06em]',
  },
  'app-store': {
    caption: 'Download on the',
    name: 'App Store',
    label: 'Download on the App Store',
    Logo: AppleLogo,
    logoClass: 'h-[26px] w-[26px]',
    captionClass: '',
  },
} as const;

/**
 * "Get it on Google Play" / "Download on the App Store" link in the stores'
 * own badge style, in the two colourways the store guidelines allow: black on
 * the light theme, white on the dark one, each with a grey hairline border.
 * The Apple logo follows the text colour; the Play logo keeps its own colours.
 *
 * Both text lines set their own `leading-none`: a Tailwind size class like
 * `text-lg` carries its own line-height, and that extra height is what pushed
 * the caption against the top border.
 */
export function StoreBadge({ store, href, className }: StoreBadgeProps) {
  const { caption, name, label, Logo, logoClass, captionClass } = STORES[store];
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={`inline-flex h-12 min-w-40 shrink-0 items-center justify-center gap-2.5 whitespace-nowrap rounded-lg border border-[#a6a6a6] bg-black px-3 text-white transition-opacity dark:bg-white dark:text-black duration-200 hover:opacity-85 ${className ?? ''}`}
    >
      <Logo aria-hidden="true" className={`shrink-0 ${logoClass}`} />
      <span className="flex flex-col items-start">
        <span className={`text-[10px] font-medium leading-none ${captionClass}`}>{caption}</span>
        {/* A space between the two lines: never drawn (a flex container skips
            white space) but it keeps the visible text "GET IT ON Google Play",
            which contains the aria-label, instead of "GET IT ONGoogle Play"
            (Lighthouse label-content-name-mismatch). axe trims each element's
            own text, so it must sit between the spans, not inside one. */}
        {' '}
        <span className="mt-1 text-[17px] font-semibold leading-none tracking-tight">{name}</span>
      </span>
    </a>
  );
}
